import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { GitCompareArrows, Plus, X, Trophy, AlertTriangle, DollarSign, Moon, Building2, MapPin } from "lucide-react";
import { toast } from "sonner";

export default function Compare() {
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/jurisdictions").then((r) => setGroups(r.data));
  }, []);

  useEffect(() => {
    if (selected.length === 0) { setData(null); return; }
    api.get(`/compare?slugs=${selected.join(",")}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        toast.error(err.response?.data?.detail || "Compare failed");
        setData(null);
      });
  }, [selected]);

  const allOptions = useMemo(() => {
    const out = [];
    for (const g of groups) for (const o of g.options) out.push({ ...o, state: g.name });
    return out;
  }, [groups]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return allOptions;
    return allOptions.filter((o) => o.name.toLowerCase().includes(s) || o.state.toLowerCase().includes(s));
  }, [q, allOptions]);

  const add = (slug) => {
    if (selected.includes(slug)) { toast.info("Already added"); return; }
    if (selected.length >= 4) { toast.error("Compare up to 4 markets at a time"); return; }
    setSelected([...selected, slug]);
    setPickerOpen(false); setQ("");
  };
  const remove = (slug) => setSelected(selected.filter((s) => s !== slug));

  return (
    <div className="p-6 md:p-10 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-slate-900 flex items-center gap-3">
            <GitCompareArrows className="w-8 h-8 text-sky-600" /> Compare markets
          </h1>
          <p className="text-slate-600 mt-2">Scout your next rental city. Pick up to 4 jurisdictions and see rules, fees, and fine risk side by side.</p>
        </div>
        <button data-testid="compare-add" onClick={() => setPickerOpen(true)}
          disabled={selected.length >= 4}
          className="inline-flex items-center gap-2 bg-slate-900 text-white px-4 py-2.5 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-40 transition">
          <Plus className="w-4 h-4" /> Add jurisdiction
        </button>
      </div>

      {selected.length === 0 && (
        <div className="border-2 border-dashed border-slate-300 rounded-2xl p-16 text-center bg-white">
          <GitCompareArrows className="w-10 h-10 text-slate-300 mx-auto" />
          <div className="mt-4 font-display text-xl text-slate-800">No markets selected yet</div>
          <p className="text-slate-500 mt-1 text-sm">Click <b>Add jurisdiction</b> to start comparing.</p>
        </div>
      )}

      {data && (
        <>
          <HighlightBar data={data} />
          <div className="overflow-x-auto -mx-6 md:-mx-10 px-6 md:px-10">
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${data.jurisdictions.length}, minmax(280px, 1fr))` }}>
              {data.jurisdictions.map((j) => (
                <Column key={j.slug} j={j} categories={data.categories} highlights={data.highlights} onRemove={() => remove(j.slug)} />
              ))}
            </div>
          </div>
        </>
      )}

      {pickerOpen && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-start justify-center z-50 p-6 pt-20" onClick={() => setPickerOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[70vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-200">
              <input autoFocus data-testid="compare-search" value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search city or state…"
                className="w-full px-4 py-3 rounded-lg bg-slate-100 border border-transparent focus:bg-white focus:border-sky-500 outline-none" />
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filtered.slice(0, 80).map((o) => (
                <button key={o.slug} data-testid={`compare-option-${o.slug}`} onClick={() => add(o.slug)}
                  className="w-full text-left px-4 py-2.5 rounded-lg hover:bg-slate-100 flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {o.jurisdiction_type === "state" ? <MapPin className="w-4 h-4 text-emerald-600 shrink-0" /> : <Building2 className="w-4 h-4 text-sky-600 shrink-0" />}
                    <div className="truncate">
                      <div className="font-medium text-slate-800 truncate">{o.name}</div>
                      <div className="text-xs text-slate-500 truncate">{o.state} · {o.ordinance}</div>
                    </div>
                  </div>
                  <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${o.jurisdiction_type === "state" ? "bg-emerald-50 text-emerald-700" : "bg-sky-50 text-sky-700"}`}>
                    {o.jurisdiction_type}
                  </span>
                </button>
              ))}
              {filtered.length === 0 && <div className="p-6 text-center text-slate-500 text-sm">No matches</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function HighlightBar({ data }) {
  const nameOf = (slug) => data.jurisdictions.find((j) => j.slug === slug)?.name || slug;
  return (
    <div className="grid md:grid-cols-3 gap-4">
      <Highlight icon={Moon} label="Strictest night cap" value={data.highlights.strictest_night_cap ? nameOf(data.highlights.strictest_night_cap) : "No caps"} tone="amber" />
      <Highlight icon={DollarSign} label="Highest max fine" value={nameOf(data.highlights.highest_fine)} tone="rose" />
      <Highlight icon={Trophy} label="Most rules" value={nameOf([...data.jurisdictions].sort((a, b) => b.rules.length - a.rules.length)[0].slug)} tone="sky" />
    </div>
  );
}

function Highlight({ icon: Icon, label, value, tone }) {
  const map = { amber: "bg-amber-50 border-amber-200 text-amber-800", rose: "bg-rose-50 border-rose-200 text-rose-800", sky: "bg-sky-50 border-sky-200 text-sky-800" };
  return (
    <div className={`border rounded-xl p-4 ${map[tone]}`}>
      <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest opacity-80"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className="mt-1.5 font-display font-bold text-lg">{value}</div>
    </div>
  );
}

function Column({ j, categories, highlights, onRemove }) {
  const rulesByCat = useMemo(() => {
    const m = {};
    for (const r of j.rules) (m[r.category || "Other"] ||= []).push(r);
    return m;
  }, [j]);
  const isStrictest = highlights.strictest_night_cap === j.slug;
  const isHighestFine = highlights.highest_fine === j.slug;

  return (
    <div data-testid={`compare-col-${j.slug}`} className="bg-white border border-slate-200 rounded-2xl overflow-hidden flex flex-col">
      <div className="relative">
        <img src={j.hero_image} alt={j.name} className="w-full h-32 object-cover" />
        <button data-testid={`compare-remove-${j.slug}`} onClick={onRemove}
          className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow">
          <X className="w-4 h-4 text-slate-700" />
        </button>
        <div className="absolute bottom-2 left-3">
          <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${j.jurisdiction_type === "state" ? "bg-emerald-500 text-white" : "bg-sky-500 text-white"}`}>
            {j.jurisdiction_type}
          </span>
        </div>
      </div>
      <div className="p-4 border-b border-slate-200">
        <div className="font-display font-bold text-lg text-slate-900">{j.name}</div>
        <div className="text-xs text-slate-500 font-mono">{j.state} · {j.ordinance}</div>
      </div>
      <div className="grid grid-cols-2 gap-2 p-4 border-b border-slate-200 text-sm">
        <Stat label="Night cap" value={j.night_cap ? `${j.night_cap}/yr` : "None"} highlight={isStrictest} />
        <Stat label="Max fine" value={`$${(j.penalty_max || 0).toLocaleString()}`} highlight={isHighestFine} />
        <Stat label="Rules" value={j.rules.length} />
        <Stat label="Summary" value={j.summary} wide />
      </div>
      <div className="p-4 space-y-4 flex-1">
        {categories.map((cat) => (
          <div key={cat}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-2">{cat}</div>
            <div className="space-y-2">
              {(rulesByCat[cat] || []).map((r) => (
                <div key={r.code} className="p-3 rounded-lg border border-slate-200 bg-slate-50">
                  <div className="text-[10px] font-mono text-slate-500">{r.code}</div>
                  <div className="text-sm font-semibold text-slate-800 mt-0.5">{r.title}</div>
                  <div className="text-xs text-slate-600 mt-1 leading-relaxed">{r.detail}</div>
                </div>
              ))}
              {(rulesByCat[cat] || []).length === 0 && (
                <div className="text-xs text-slate-400 italic py-2">Not addressed</div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight, wide }) {
  return (
    <div className={`${wide ? "col-span-2" : ""} ${highlight ? "bg-amber-50 border border-amber-200 rounded-lg p-2 -m-2" : ""}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={`mt-0.5 ${wide ? "text-xs text-slate-700 line-clamp-3" : "font-semibold text-slate-800"}`}>{value}</div>
      {highlight && <div className="mt-1 flex items-center gap-1 text-[10px] font-mono uppercase text-amber-700"><AlertTriangle className="w-3 h-3" /> Strictest</div>}
    </div>
  );
}
